import React, { useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import Modal from 'components/GeneralComponents/Modal';
import Button from 'components/GeneralComponents/Button';
import { Input } from 'components/StyledComponents/Input/Input';
import TimePicker from 'rc-time-picker';
import 'rc-time-picker/assets/index.css';
import { shiftTemplateService } from './shiftTemplateService';
import moment from 'moment';
import { isValidArray } from 'helpers/validation';

// Import the same field components used in ShiftFieldsComponent
import DetailFieldTimeComponent from '../components/TableComponents/ShiftDetail/DetailFieldTimeComponent';
import DetailFieldPositionComponent from '../components/TableComponents/ShiftDetail/DetailFieldPositionComponent';
import DetailFieldQuantityComponent from '../components/TableComponents/ShiftDetail/DetailFieldQuantityComponent';
import DetailFieldPostToGroupComponent from '../components/TableComponents/ShiftDetail/DetailFieldPostToGroupComponent';
import DetailFieldDescriptionComponent from '../components/TableComponents/ShiftDetail/DetailFieldDescriptionComponent';
import DetailFieldSelectWeekdayComponent from '../components/TableComponents/ShiftDetail/DetailFieldSelectWeekdayComponent';

// Memoized TimePicker components to prevent unnecessary re-renders
const MemoizedTimePicker = React.memo(TimePicker);

const CreateTemplateModal = ({ visible, onCancel, onSuccess, locationId, template = null, channels }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    shift_template_shifts: [],
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [validationError, setValidationError] = useState('');
  const [openDropdownIdx, setOpenDropdownIdx] = useState(null);
  const isEditing = !!template;

  // Memoize the default shift object to avoid recreating it on every render
  const defaultShift = useMemo(() => ({
    shift_quantity: 1,
    start_time: '',
    end_time: '',
    position: '',
    group: null,
    shift_description: '',
    recurrence: []
  }), []);

  React.useEffect(() => {
    if (visible) {
      console.time('Modal Initialization');
      if (template) {
        setForm({
          name: template.name,
          description: template.description,
          shift_template_shifts: template.shift_template_shifts?.map(shift => {
            let groupObj = null;
            if (shift.group_id && Array.isArray(channels)) {
              groupObj = channels.find(g => g.id === shift.group_id) || null;
            }
            return {
              ...shift,
              start_time: shift.start_time,
              end_time: shift.end_time,
              group: groupObj,
              location: shift.location || '',
            };
          }) || []
        });
      } else {
        setForm({
          name: '',
          description: '',
          shift_template_shifts: []
        });
      }
      console.timeEnd('Modal Initialization');
    }
  }, [visible, template, channels]);

  // Optimized click outside handler with useCallback
  const handleClickOutside = useCallback((event) => {
    if (openDropdownIdx !== null) {
      const target = event.target;
      const isDropdownClick = target.closest('.group-dropdown-menu');
      const isButtonClick = target.closest('.group-dropdown-btn');
      
      if (!isDropdownClick && !isButtonClick) {
        setOpenDropdownIdx(null);
      }
    }
  }, [openDropdownIdx]);

  React.useEffect(() => {
    if (openDropdownIdx !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openDropdownIdx, handleClickOutside]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 2000);
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleShiftChange = useCallback((index, field, value) => {
    console.time(`Update Shift Field ${index}.${field}`);
    setForm(prev => {
      // Use functional update to ensure we're working with the latest state
      const shifts = [...prev.shift_template_shifts];
      if (shifts[index]) {
        shifts[index] = { ...shifts[index], [field]: value };
      }
      const newForm = { ...prev, shift_template_shifts: shifts };
      console.timeEnd(`Update Shift Field ${index}.${field}`);
      return newForm;
    });
  }, []);

  // Time picker handlers (matching DetailFieldTimeComponent pattern)
  const handleStartTimeChange = useCallback((index, time) => {
    if (time) {
      const timeString = time.format('hh:mm A');
      // Debounce the state update to reduce re-renders
      setTimeout(() => {
        handleShiftChange(index, 'start_time', timeString);
      }, 0);
    }
  }, [handleShiftChange]);

  const handleEndTimeChange = useCallback((index, time) => {
    if (time) {
      const timeString = time.format('hh:mm A');
      // Debounce the state update to reduce re-renders
      setTimeout(() => {
        handleShiftChange(index, 'end_time', timeString);
      }, 0);
    }
  }, [handleShiftChange]);

  // Group selection handler (matching DetailFieldPostToGroupComponent pattern)
  const handleGroupSelect = useCallback((index, groupObj) => {
    console.time(`Select Group ${index}`);
    handleShiftChange(index, 'group', groupObj);
    setOpenDropdownIdx(null);
    console.timeEnd(`Select Group ${index}`);
  }, [handleShiftChange]);

  // Job title handler (matching DetailFieldPositionComponent pattern)
  const handleJobTitleChange = useCallback((index, event) => {
    const val = event.target.value;
    handleShiftChange(index, 'position', val);
  }, [handleShiftChange]);

  // Quantity handler (matching DetailFieldQuantityComponent pattern)
  const handleQuantityChange = useCallback((index, event) => {
    const value = event.target.value;
    const isValid = /^\d+$/.test(value);
    if (!isValid) {
      event.preventDefault();
      event.target.value = value.replace(/[^0-9]/g, '');
      return;
    }
    if (value > 9999) {
      event.preventDefault();
      event.target.value = value.slice(0, -1);
      return;
    }
    handleShiftChange(index, 'shift_quantity', parseInt(value) || 1);
  }, [handleShiftChange]);

  // Description handler (matching DetailFieldDescriptionComponent pattern)
  const handleDescriptionChange = useCallback((index, event) => {
    handleShiftChange(index, 'shift_description', event.target.value);
  }, [handleShiftChange]);

  // Weekday selection handler (matching DetailFieldSelectWeekdayComponent pattern)
  const handleWeekdayChange = useCallback((index, selectedDays) => {
    handleShiftChange(index, 'recurrence', selectedDays);
  }, [handleShiftChange]);

  const addShift = useCallback(() => {
    console.time('Add Shift');
    setForm(prev => {
      const newForm = {
        ...prev,
        shift_template_shifts: [
          ...prev.shift_template_shifts,
          { ...defaultShift }
        ]
      };
      console.timeEnd('Add Shift');
      return newForm;
    });
  }, [defaultShift]);

  const removeShift = useCallback((index) => {
    console.time(`Remove Shift ${index}`);
    setForm(prev => {
      const shifts = [...prev.shift_template_shifts];
      shifts.splice(index, 1);
      const newForm = { ...prev, shift_template_shifts: shifts };
      console.timeEnd(`Remove Shift ${index}`);
      return newForm;
    });
  }, []);

  const toggleDropdown = useCallback((idx) => {
    console.time(`Toggle Dropdown ${idx}`);
    setOpenDropdownIdx(openDropdownIdx === idx ? null : idx);
    console.timeEnd(`Toggle Dropdown ${idx}`);
  }, [openDropdownIdx]);

  const toggleWeekday = useCallback((index, dayKey) => {
    console.time(`Toggle Weekday ${dayKey}`);
    setForm(prev => {
      const shifts = [...prev.shift_template_shifts];
      const rec = Array.isArray(shifts[index].recurrence) ? [...shifts[index].recurrence] : [];
      if (rec.includes(dayKey)) {
        shifts[index].recurrence = rec.filter(d => d !== dayKey);
      } else {
        shifts[index].recurrence = [...rec, dayKey];
      }
      const newForm = { ...prev, shift_template_shifts: shifts };
      console.timeEnd(`Toggle Weekday ${dayKey}`);
      return newForm;
    });
  }, []);

  const validateForm = useCallback(() => {
    if (!form.name.trim()) return 'Template name is required.';
    if (!locationId) return 'Location is required.';
    if (!Array.isArray(form.shift_template_shifts) || form.shift_template_shifts.length === 0) return 'At least one shift is required.';
    for (let i = 0; i < form.shift_template_shifts.length; i++) {
      const shift = form.shift_template_shifts[i];
      if (!shift.shift_quantity || shift.shift_quantity < 1) return `Shift ${i + 1}: Quantity must be at least 1.`;
      if (!shift.start_time) return `Shift ${i + 1}: Start time is required.`;
      if (!shift.end_time) return `Shift ${i + 1}: End time is required.`;
      if (!shift.position) return `Shift ${i + 1}: Position is required.`;
      if (!shift.group) return `Shift ${i + 1}: Group is required.`;
      if (!Array.isArray(shift.recurrence) || shift.recurrence.length === 0) return `Shift ${i + 1}: At least one recurrence day is required.`;
    }
    return '';
  }, [form, locationId]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    console.time('Form Submission');
    setValidationError('');
    const error = validateForm();
    if (error) {
      setValidationError(error);
      showToast(error, 'error');
      console.timeEnd('Form Submission');
      return;
    }
    setLoading(true);
    try {
      const templateData = {
        name: form.name,
        description: form.description,
        location_id: locationId,
        shift_template_shifts_attributes: form.shift_template_shifts.map(shift => ({
          shift_quantity: shift.shift_quantity,
          start_time: shift.start_time,
          end_time: shift.end_time,
          position: shift.position,
          group_id: shift.group?.id || '',
          shift_description: shift.shift_description || '',
          recurrence: Array.isArray(shift.recurrence) ? shift.recurrence : [],
        }))
      };
      if (isEditing) {
        await shiftTemplateService.updateTemplate(template.id, templateData);
        showToast('Template updated successfully');
      } else {
        await shiftTemplateService.createTemplate(templateData);
        showToast('Template created successfully');
      }
      onSuccess();
    } catch (error) {
      showToast(error?.message || 'Failed to save template', 'error');
    } finally {
      setLoading(false);
      console.timeEnd('Form Submission');
    }
  }, [form, locationId, isEditing, template, validateForm, showToast, onSuccess]);

  const handleDelete = useCallback(async () => {
    if (!isEditing || !template) return;
    
    if (window.confirm('Are you sure you want to delete this template?')) {
      setLoading(true);
      try {
        await shiftTemplateService.deleteTemplate(template.id);
        showToast('Template deleted successfully');
        onSuccess();
      } catch (error) {
        showToast(error?.message || 'Failed to delete template', 'error');
      } finally {
        setLoading(false);
      }
    }
  }, [isEditing, template, showToast, onSuccess]);

  // Performance monitoring for setInterval violations
  React.useEffect(() => {
    const originalSetInterval = window.setInterval;
    let violationCount = 0;
    
    window.setInterval = function(...args) {
      const startTime = performance.now();
      const intervalId = originalSetInterval.apply(this, args);
      
      // Monitor for long-running intervals
      const checkInterval = () => {
        const elapsed = performance.now() - startTime;
        if (elapsed > 16) { // 16ms = 60fps threshold
          violationCount++;
          console.warn(`setInterval violation #${violationCount}: ${elapsed.toFixed(2)}ms`, args);
        }
      };
      
      // Check after a short delay
      setTimeout(checkInterval, 20);
      
      return intervalId;
    };
    
    return () => {
      window.setInterval = originalSetInterval;
    };
  }, []);

  return (
    <div className="modern-template-modal__wrap best-in-class-modal">
      <form onSubmit={handleSubmit}>
        <div className="modal-title-row">
          <h2 className="modal-title">{isEditing ? 'Edit Template' : 'Create New Template'}</h2>
        </div>
        {validationError && <div style={{ color: '#dc3545', fontWeight: 600, marginBottom: 12 }}>{validationError}</div>}
        
        {/* Template-level fields */}
        <div className="modal-fields-row grid-2-cols">
          <div className="modal-field-col">
            <label>Template Name</label>
            <Input name="name" value={form.name} onChange={handleChange} placeholder="e.g., Weekend Coverage" required />
          </div>
          <div className="modal-field-col">
            <label>Description</label>
            <Input name="description" value={form.description} onChange={handleChange} placeholder="Brief description of this template" />
          </div>
        </div>

        <div className="modal-section-header-row">
          <h3>Shifts in Template</h3>
          <Button action={addShift} text="+ Add Shift" type="primary" />
        </div>

        {/* Shifts list using ShiftFieldsComponent-style layout */}
        <div className="shifts-list">
          {form.shift_template_shifts.map((shift, idx) => {
            // Create a mock date for the time components
            const mockDate = moment();
            
            return (
              <div key={idx} className="shift-fields-container" style={{ 
                border: '1px solid #e1e5e9', 
                borderRadius: 12, 
                padding: 24, 
                marginBottom: 24,
                background: '#fff'
              }}>
                <div className="shift-card-header" style={{ marginBottom: 18 }}>
                  <span className="shift-title" style={{ fontSize: 18, fontWeight: 600 }}>Shift {idx + 1}</span>
                  <Button action={() => removeShift(idx)} text="Remove" type="danger" />
                </div>

                {/* Top row: Quantity and Position (like ShiftFieldsComponent) */}
                <div className="flex-row top-row" style={{ display: 'flex', gap: 24, marginBottom: 18 }}>
                  <div className="quantity-wrap" style={{ minWidth: 120 }}>
                    <div className="title-label" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#232323' }}>Quantity</div>
                    <input 
                      className="quantity-input" 
                      type="number"
                      min={1}
                      value={shift.shift_quantity || 1}
                      onChange={e => handleQuantityChange(idx, e)}
                      style={{ 
                        width: 80, 
                        borderRadius: 8, 
                        border: '1px solid #e0e0e0', 
                        padding: '8px 10px', 
                        fontSize: 15 
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="title-label" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#232323' }}>Position</div>
                    <input 
                      className="job-box" 
                      type="text" 
                      value={shift.position || ''}
                      onChange={e => handleJobTitleChange(idx, e)}
                      placeholder="e.g., Cashier, Manager"
                      style={{ 
                        width: '100%', 
                        borderRadius: 8, 
                        border: '1px solid #e0e0e0', 
                        padding: '8px 10px', 
                        fontSize: 15 
                      }}
                    />
                  </div>
                </div>

                {/* Time row (using DetailFieldTimeComponent pattern) */}
                <div style={{ marginBottom: 18 }}>
                  <div className="title-label" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#232323' }}>Time</div>
                  <div className="time-row flex-row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <MemoizedTimePicker
                      onChange={time => handleStartTimeChange(idx, time)}
                      defaultValue={shift.start_time ? moment(shift.start_time, 'hh:mm A') : null}
                      inputReadOnly={true}
                      allowEmpty={false}
                      use12Hours={true}
                      showSecond={false}
                      minuteStep={15}
                      hideDisabledOptions={true}
                      style={{ flex: 1 }}
                      placeholder="Start time"
                      clearIcon={null}
                      showHour={true}
                      showMinute={true}
                      format="hh:mm A"
                      disabled={false}
                      autoFocus={false}
                      onOpen={() => console.time(`TimePicker Open ${idx} Start`)}
                      onClose={() => console.timeEnd(`TimePicker Open ${idx} Start`)}
                    />
                    <span className="picker-divider" style={{ fontSize: 16, color: '#666' }}>to</span>
                    <MemoizedTimePicker
                      onChange={time => handleEndTimeChange(idx, time)}
                      defaultValue={shift.end_time ? moment(shift.end_time, 'hh:mm A') : null}
                      inputReadOnly={true}
                      allowEmpty={false}
                      use12Hours={true}
                      showSecond={false}
                      minuteStep={15}
                      hideDisabledOptions={true}
                      style={{ flex: 1 }}
                      placeholder="End time"
                      clearIcon={null}
                      showHour={true}
                      showMinute={true}
                      format="hh:mm A"
                      disabled={false}
                      autoFocus={false}
                      onOpen={() => console.time(`TimePicker Open ${idx} End`)}
                      onClose={() => console.timeEnd(`TimePicker Open ${idx} End`)}
                    />
                  </div>
                </div>

                {/* Group and Description row (like ShiftFieldsComponent) */}
                <div style={{ marginBottom: 18 }}>
                  <div className="title-label" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#232323' }}>Group</div>
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className="group-dropdown-btn"
                      onClick={() => toggleDropdown(idx)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: '#fff',
                        border: '1px solid #e1e5e9',
                        borderRadius: 10,
                        padding: '14px 16px',
                        cursor: 'pointer',
                        fontSize: 16,
                        color: '#232323',
                        width: '100%',
                        transition: 'border 0.2s, box-shadow 0.2s'
                      }}
                    >
                      {shift.group?.channel_name || 'Select group'}
                      <span style={{ fontSize: 16, color: '#b0b0b0' }}>
                        {openDropdownIdx === idx ? '▲' : '▼'}
                      </span>
                    </button>
                    {openDropdownIdx === idx && (
                      <div 
                        className="group-dropdown-menu"
                        style={{ 
                          position: 'absolute', 
                          zIndex: 10000, 
                          minWidth: '100%', 
                          maxHeight: '260px', 
                          overflowY: 'auto',
                          background: '#fff',
                          border: '1px solid #e1e5e9',
                          borderRadius: 8,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }}
                      >
                        {channels?.map(element => (
                          <div
                            key={element.id}
                            onClick={() => handleGroupSelect(idx, element)}
                            style={{ 
                              cursor: 'pointer', 
                              padding: '12px 16px',
                              borderBottom: '1px solid #f0f0f0',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={e => e.target.style.background = '#f8f9fa'}
                            onMouseLeave={e => e.target.style.background = '#fff'}
                          >
                            <div style={{ fontWeight: 500 }}>{element.channel_name}</div>
                            <div style={{ fontSize: 12, color: '#666' }}>+{element.member_count} members</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description (using DetailFieldDescriptionComponent pattern) */}
                <div style={{ marginBottom: 18 }}>
                  <div className="flex-row desc-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div className="title-label" style={{ fontSize: 16, fontWeight: 600, color: '#232323' }}>Shift Description</div>
                  </div>
                  <textarea 
                    className="shift-description" 
                    type="textarea" 
                    placeholder="Enter shift description" 
                    value={shift.shift_description || ''}
                    onChange={e => handleDescriptionChange(idx, e)}
                    style={{ 
                      width: '100%', 
                      minHeight: 60, 
                      borderRadius: 8, 
                      border: '1px solid #e0e0e0', 
                      padding: '12px', 
                      fontSize: 15,
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Recurrence (using DetailFieldSelectWeekdayComponent pattern) */}
                <div>
                  <div className="title-label" style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#232323' }}>Recurrence (Apply to)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => {
                      const dayKey = day.toLowerCase().substring(0, 3);
                      const isSelected = Array.isArray(shift.recurrence) && shift.recurrence.includes(dayKey);
                      return (
                        <button
                          key={dayKey}
                          type="button"
                          onClick={() => toggleWeekday(idx, dayKey)}
                          style={{
                            background: isSelected ? '#2c5eff' : '#f4f4f4',
                            color: isSelected ? '#fff' : '#222',
                            border: 'none',
                            borderRadius: 20,
                            padding: '8px 16px',
                            fontWeight: 600,
                            fontSize: 14,
                            cursor: 'pointer',
                            transition: 'background 0.18s, color 0.18s'
                          }}
                        >
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Action buttons (matching ShiftFieldsComponent button-row pattern) */}
        <div className="button-row" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginTop: 32,
          paddingTop: 24,
          borderTop: '1px solid #e1e5e9'
        }}>
          <div>
            {isEditing && (
              <button 
                className="delete-button" 
                onClick={handleDelete}
                style={{
                  background: '#dc3545',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px 24px',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
              >
                Delete Template
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button 
              className="cancel-button" 
              onClick={onCancel}
              style={{
                background: '#f8f9fa',
                color: '#232323',
                border: '1px solid #e1e5e9',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
            >
              Cancel
            </button>
            <button 
              className="save-button" 
              onClick={handleSubmit}
              disabled={loading}
              style={{
                background: '#2c5eff',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '12px 24px',
                fontSize: 15,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transition: 'background 0.2s'
              }}
            >
              {loading ? 'Saving...' : (isEditing ? 'Update Template' : 'Create Template')}
            </button>
          </div>
        </div>
      </form>
      {toast.show && <div className={`toast-message ${toast.type}`}>{toast.message}</div>}
    </div>
  );
};

const mapStateToProps = (state) => ({
  channels: state.appReducer.channels.data,
});

export default connect(mapStateToProps)(CreateTemplateModal); 